import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';

import {
  ContentState,
  convertToRaw,
  EditorState,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { Editor } from 'react-draft-wysiwyg';

import Header from '../../../../components/Header/Header';
import { toolbarMango } from '../../../../shared/data';
import { TQ_VALIDATION } from '../../constants';

const DESCRIPTION_PLACEHOLDER =
  'Describe what you need support with, any goals or context.';

const block = 'create-service-request';

const DESCRIPTION_MAX_LENGTH =
  TQ_VALIDATION.MAX_SERVICE_REQUEST_DESCRIPTION_LENGTH;

function createEditorStateWithCursorAtEnd(html: string): EditorState {
  if (!html || !html.trim()) {
    return EditorState.createEmpty();
  }
  const blocksFromHtml = htmlToDraft(html);
  const { contentBlocks, entityMap } = blocksFromHtml;
  const contentState = ContentState.createFromBlockArray(
    contentBlocks,
    entityMap,
  );
  const editorState = EditorState.createWithContent(contentState);
  return EditorState.moveSelectionToEnd(editorState);
}

export interface ServiceRequestDescriptionEditorProps {
  initialDescriptionHtml: string;
  onDone: (descriptionHtml: string) => void;
  onClose: () => void;
}

const ServiceRequestDescriptionEditor: React.FC<
  ServiceRequestDescriptionEditorProps
> = ({ initialDescriptionHtml, onDone, onClose }) => {
  const [editorState, setEditorState] = useState(() =>
    createEditorStateWithCursorAtEnd(initialDescriptionHtml),
  );

  const lastValidStateRef = useRef<EditorState>(editorState);

  // Force cursor to end after Editor mounts (react-draft-wysiwyg can reset selection on first paint)
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setEditorState((prev) => {
        const next = EditorState.moveSelectionToEnd(prev);
        lastValidStateRef.current = next;
        return next;
      });
    });
    return () => cancelAnimationFrame(id);
  }, []);


  const onEditorStateChange = useCallback((state: EditorState) => {
    const plainText = state.getCurrentContent().getPlainText();
    const lastValidLen = lastValidStateRef.current
      .getCurrentContent()
      .getPlainText().length;

    if (plainText.length > DESCRIPTION_MAX_LENGTH) {
      // Already at limit: reject any new character (no matter where cursor is)
      if (lastValidLen === DESCRIPTION_MAX_LENGTH) {
        flushSync(() => {
          setEditorState(lastValidStateRef.current);
        });
        return;
      }
      // Was under limit (e.g. paste): truncate to max
      const truncatedText = plainText.slice(0, DESCRIPTION_MAX_LENGTH);
      const contentState = ContentState.createFromText(truncatedText);
      const truncatedState = EditorState.moveSelectionToEnd(
        EditorState.createWithContent(contentState),
      );
      lastValidStateRef.current = truncatedState;
      setTimeout(() => {
        setEditorState(truncatedState);
      }, 0);
      return;
    }
    lastValidStateRef.current = state;
    setEditorState(state);
  }, []);

  // Block paste only when already at limit; otherwise allow and truncate in onEditorStateChange
  const handlePastedText = useCallback(
    (
      _text: string,
      _html: string,
      state: EditorState,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- required by EditorProps signature
      _onChange: (editorState: EditorState) => void,
    ): boolean => {
      const current = state.getCurrentContent().getPlainText();
      return current.length >= DESCRIPTION_MAX_LENGTH;
    },
    [],
  );

  const descriptionPlainText = editorState.getCurrentContent().getPlainText();
  const isDoneActive = descriptionPlainText.trim().length > 0;

  const handleDone = useCallback(() => {
    const contentState = editorState.getCurrentContent();
    const rawContentState = convertToRaw(contentState);
    const html = draftToHtml(rawContentState);
    onDone(html || '');
  }, [editorState, onDone]);

  return (
    <div className={`${block} ${block}--editor page__container`}>
      <Header
        title="Description"
        handleBack={onClose}
        backType="cross"
        actionItems={[
          <button
            key="done"
            type="button"
            className={`${block}__header-done ${
              isDoneActive ? `${block}__header-done--active` : ''
            }`}
            disabled={!isDoneActive}
            onClick={handleDone}>
            Done
          </button>,
        ]}
      />
      <div
        className={`${block}__editor-content ${block}__editor-content--description`}>
        <Editor
          editorState={editorState}
          toolbar={toolbarMango}
          stripPastedStyles
          placeholder={DESCRIPTION_PLACEHOLDER}
          toolbarClassName="toolbarClassName"
          wrapperClassName="emailBodyEditorWrapper"
          editorClassName="editorClassName"
          onEditorStateChange={onEditorStateChange}
          handlePastedText={handlePastedText}
        />
        <div
          className={`${block}__char-count ${
            descriptionPlainText.length >= DESCRIPTION_MAX_LENGTH - 10
              ? `${block}__char-count--near-limit`
              : descriptionPlainText.length > 0
                ? `${block}__char-count--active`
                : ''
          }`}>
          {descriptionPlainText.length}/{DESCRIPTION_MAX_LENGTH}
        </div>
      </div>
      <div className={`${block}__editor-keyboard-spacer`} />
    </div>
  );
};

export default ServiceRequestDescriptionEditor;
